<script lang="ts" setup>
import { computed } from 'vue';
import { computedAsync } from '@vueuse/core';

import { usePriceCalendarStore } from '@apps/main/new-desktop/core/stores';
import type { EditTripSegment } from '@apps/main/new-desktop/modules/editTripModal';
import type {
  AutoCompleteType,
  DestinationAutocompleteType,
} from '@apps/main/new-desktop/modules/autoComplete';
import { PaxTab } from '@apps/main/new-desktop/modules/paxSelector';
import { PriceCalendar } from '@apps/main/new-desktop/modules/priceCalendar';
import { TabKey } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore.model';
import { useConfigurationPanelStore } from '@apps/main/new-desktop/core/stores/configurationPanel/configurationPanelStore';
import { CoreEventSource } from '@apps/main/new-desktop/core/models';
import { CalendarTab } from '@apps/main/new-desktop/modules/priceRibbon';

import type {
  AutoCompleteSuggestion,
  IngredientId,
  Pax,
} from '@libs/trip-planner-interfaces';
import { AppButton, AppTabs } from '@libs/ui';
import { isExperimentAsync } from '@libs/feature-toggle';

import { EditTripStepType } from '../models/editCarTripView.model';
import { useCarTripPageStore } from '../stores/carTripPageStore';

import EditCarTripViewContent from './editCarTrip/EditCarTripViewContent.vue';

interface CarTripConfigurationPanelEmits {
  (eventName: 'update-pax', newPax: Pax): void;
  (eventName: 'add-destination', suggestion: AutoCompleteSuggestion): void;
  (
    eventName: 'change-destination',
    {
      suggestion,
      ingredientId,
      autocomplete,
    }: {
      suggestion: AutoCompleteSuggestion;
      ingredientId: IngredientId;
      autocomplete:
        | AutoCompleteType.Destination
        | AutoCompleteType.StartingPosition
        | AutoCompleteType.EndingPosition;
    }
  ): void;
  (eventName: 'change-trip-order', segments: EditTripSegment[]): void;
  (eventName: 'change-number-of-nights', segment: EditTripSegment): void;
  (eventName: 'remove-destination', segment: EditTripSegment): void;
  (
    eventName: 'select-my-position',
    autocompleteType: DestinationAutocompleteType
  ): void;
  (eventName: 'month-selected', value: Date): void;
}

const emit = defineEmits<CarTripConfigurationPanelEmits>();

const configurationPanelStore = useConfigurationPanelStore();
const carTripPageStore = useCarTripPageStore();
const priceCalendarStore = usePriceCalendarStore();

function changeEditStepToOverview(): void {
  configurationPanelStore.changeEditStep({
    stepType: EditTripStepType.Overview,
  });
}

function onSelectMonth(dayInMonth: Date): void {
  priceCalendarStore.selectItineraryMonth(
    dayInMonth,
    CoreEventSource.MonthsSelector
  );
}

function onSelectDate(date: Date): void {
  priceCalendarStore.selectItineraryDate(date, CoreEventSource.MonthsSelector);
}

function onChangeTab(tabKey: string): void {
  const key = Object.values(TabKey).find((key) => key === tabKey);
  if (
    configurationPanelStore.getTabSelected === TabKey.Itinerary &&
    configurationPanelStore.getCurrentStep.stepType !==
      EditTripStepType.Overview
  ) {
    changeEditStepToOverview();
  }
  if (key) {
    carTripPageStore.handleTabSwitch(key);
  }
}

const shouldShowBackButton = computed<boolean>(
  () =>
    configurationPanelStore.getCurrentStep.stepType ===
      EditTripStepType.UpdateDestination ||
    configurationPanelStore.getCurrentStep.stepType ===
      EditTripStepType.AddDestination
);

const isCalendarWithoutPricesInCarTripExperiment = computedAsync(
  () => isExperimentAsync('calendar_without_prices_in_car_trip'),
  false
);
</script>

<template>
  <div
    class="car-trip-configuration-panel"
    :class="{
      calendarExperiment:
        configurationPanelStore.getTabSelected === TabKey.Calendar &&
        isCalendarWithoutPricesInCarTripExperiment,
    }"
  >
    <div class="car-trip-configuration-panel-tabs-wrapper">
      <div class="configuration-panel-tab-selector-wrapper">
        <AppTabs
          :tabs="configurationPanelStore.getCarTripConfigurationPanelTabs"
          :selected-tab-key="configurationPanelStore.getTabSelected"
          type="wide"
          @tab-click="onChangeTab"
        />
      </div>
    </div>
    <div class="tabs-content">
      <PaxTab
        v-if="
          configurationPanelStore.getPaxDetails &&
          configurationPanelStore.getTabSelected === TabKey.Pax
        "
        :pax-details="configurationPanelStore.getPaxDetails"
        @update-pax="emit('update-pax', $event)"
      />
      <template
        v-if="configurationPanelStore.getTabSelected === TabKey.Calendar"
      >
        <div
          v-if="isCalendarWithoutPricesInCarTripExperiment"
          class="price-calendar-wrapper"
        >
          <PriceCalendar
            :selected-month="priceCalendarStore.selectedMonthOption.id"
            :months="priceCalendarStore.priceRibbonMonths"
            :price-calendar-state="priceCalendarStore.priceCalendarState"
            :show-prices="!isCalendarWithoutPricesInCarTripExperiment"
            :allow-selection-for="[]"
            @select-date="onSelectDate"
            @select-month="onSelectMonth"
          />
        </div>
        <CalendarTab
          v-else
          :months="priceCalendarStore.priceRibbonMonths"
          :selected-month-iso-date="priceCalendarStore.selectedMonthOption.id"
          @confirm="emit('month-selected', $event)"
        />
      </template>
      <EditCarTripViewContent
        v-if="
          configurationPanelStore.editTripCarDetails &&
          configurationPanelStore.getTabSelected === TabKey.Itinerary
        "
        :segments="configurationPanelStore.editTripCarDetails.segments"
        :initial-step="configurationPanelStore.editTripCarDetails.initialStep"
        @add-destination="emit('add-destination', $event)"
        @change-destination="emit('change-destination', $event)"
        @change-trip-order="emit('change-trip-order', $event)"
        @change-number-of-nights="emit('change-number-of-nights', $event)"
        @remove-destination="emit('remove-destination', $event)"
        @select-my-position="emit('select-my-position', $event)"
        @change-step="configurationPanelStore.changeEditStep"
      />
    </div>
    <div
      v-if="shouldShowBackButton"
      class="car-trip-configuration-panel-button"
    >
      <AppButton
        state="enabled"
        class="back"
        type="primary"
        size="large"
        @click="changeEditStepToOverview"
      >
        {{ $t('planner.back') }}
      </AppButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.car-trip-configuration-panel {
  border-right: 1px solid var(--color-scrollbar);
  height: 100%;
  gap: var(--spacing-40);
  display: flex;
  flex-direction: column;
  position: relative;
  @include for-less-or-equal-than-899-px {
    gap: var(--spacing-16);
  }
  .car-trip-configuration-panel-tabs-wrapper {
    @include for-more-or-equal-than-599-px {
      padding-top: var(--spacing-24);
    }
    display: flex;
    width: 100%;
    .configuration-panel-tab-selector-wrapper {
      position: relative;
      width: 100%;
      margin: 0 var(--spacing-24);
      @include for-less-or-equal-than-1199-px {
        margin: 0;
      }
    }
  }

  .tabs-content {
    //NOTE: This variable is needed to position collapsed map
    --side-padding: var(--spacing-24);
    height: 100%;
    overflow-y: auto;
    padding: 0 var(--side-padding);
    margin-top: var(--gap);

    .price-calendar-wrapper {
      height: 100%;
      max-width: 600px;
      padding: var(--spacing-24);

      @include for-less-or-equal-than-599-px {
        padding: var(--spacing-24) var(--spacing-8);
      }
    }
  }

  .car-trip-configuration-panel-button {
    flex: 1;
    display: flex;
    width: 100%;
    align-items: end;
    .back {
      margin-right: var(--spacing-24);
      margin-left: var(--spacing-24);
      margin-bottom: var(--spacing-24);
      width: inherit;
    }
  }
}
.calendarExperiment {
  gap: var(--spacing-8);
  .tabs-content {
    @include for-less-or-equal-than-899-px {
      padding: 0;
    }
  }
}
</style>
